import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Clock, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import { CONTACT } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  "Interior Painting",
  "Exterior Painting",
  "Pressure Washing",
  "Interior + Exterior",
  "Other / Not Sure",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: services[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data?.status === "success") {
        setSubmitted(true);
        toast.success(res.data.message || "Request received!");
        setForm({ name: "", email: "", phone: "", service: services[0], message: "" });
      } else {
        toast.error("Something went wrong. Please call us at 850-541-6842.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Couldn’t send right now. Please call 850-541-6842.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#334155] text-white py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
        {/* Left – info */}
        <div className="lg:col-span-5">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#0EA5E9]">Free In-Person Estimate</div>
          <h2 className="mt-3 font-heading font-black uppercase tracking-tight text-4xl sm:text-5xl leading-[0.95]">
            Get A Quote.<br />Usually Same Day.
          </h2>
          <p className="mt-5 text-base text-[#CBD5E1] leading-relaxed max-w-md">
            Tell us a little about your project. We’ll come out for a free in-person
            walk-through and quote it on the spot — no obligation, no high-pressure sales.
            Serving Bay County since 2017.
          </p>

          <div className="mt-10 space-y-5">
            <InfoRow icon={Phone} label="Call or Text" value="850-541-6842" href="tel:8505416842" />
            <InfoRow icon={Mail} label="Email" value="jjhpaintingpw@gmail.com" href="mailto:jjhpaintingpw@gmail.com" />
            <InfoRow icon={Clock} label="Hours" value="Mon – Sat · 8:00a – 5:00p" />
            <InfoRow icon={MapPin} label="Service Area" value="All of Bay County, FL (except Tyndall AFB)" />
          </div>
        </div>

        {/* Right – form */}
        <div className="lg:col-span-7">
          {submitted ? (
            <div
              data-testid={CONTACT.success}
              className="bg-white text-[#334155] border-2 border-[#0EA5E9] p-8 sm:p-12"
            >
              <CheckCircle2 size={48} className="text-[#0EA5E9]" />
              <h3 className="mt-6 font-heading font-black uppercase tracking-tight text-3xl">
                Request Received.
              </h3>
              <p className="mt-3 text-[#4B5563] max-w-md">
                Thanks for reaching out. A member of the JJH crew will be in touch within
                one business day. Need to talk sooner? Call us at{" "}
                <a href="tel:8505416842" className="font-bold underline">850-541-6842</a>.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="contact-send-another"
                className="mt-8 inline-flex items-center gap-2 bg-[#334155] text-white px-6 py-3 font-heading font-bold uppercase tracking-wider hover:bg-[#475569]"
              >
                Send Another <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <form
              data-testid={CONTACT.form}
              onSubmit={submit}
              className="bg-white text-[#334155] p-6 sm:p-10 space-y-5 border-2 border-[#0EA5E9]"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name" testId={CONTACT.name}>
                  <input
                    data-testid={CONTACT.name}
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    className={inputClass}
                    required
                  />
                </Field>
                <Field label="Phone" testId={CONTACT.phone}>
                  <input
                    data-testid={CONTACT.phone}
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(850) 555-0123"
                    className={inputClass}
                    required
                  />
                </Field>
              </div>
              <Field label="Email" testId={CONTACT.email}>
                <input
                  data-testid={CONTACT.email}
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  className={inputClass}
                  required
                />
              </Field>
              <Field label="Service Needed" testId={CONTACT.service}>
                <select
                  data-testid={CONTACT.service}
                  value={form.service}
                  onChange={update("service")}
                  className={`${inputClass} appearance-none bg-white`}
                >
                  {services.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Project Details" testId={CONTACT.message}>
                <textarea
                  data-testid={CONTACT.message}
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your home, square footage, timing, etc."
                  className={`${inputClass} resize-y`}
                  required
                />
              </Field>

              <button
                data-testid={CONTACT.submit}
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#0EA5E9] hover:bg-[#0284C7] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 font-heading font-bold uppercase tracking-wider transition-colors"
              >
                {submitting ? "Sending…" : "Request Free Estimate"}
                {!submitting && <ArrowRight size={18} />}
              </button>
              <p className="text-xs text-[#4B5563]">
                We’ll never share your info. By submitting, you agree to be contacted about your estimate.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const inputClass =
  "w-full border-2 border-[#334155] bg-white px-4 py-3 text-[#334155] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] transition-colors text-base";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-[0.2em] text-[#334155] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const Inner = (
    <div className="flex items-start gap-4 group">
      <div className="h-11 w-11 grid place-items-center bg-[#0EA5E9] text-white shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#94A3B8]">{label}</div>
        <div className="mt-1 font-heading font-bold uppercase tracking-tight text-white text-lg sm:text-xl group-hover:text-[#0EA5E9] transition-colors">
          {value}
        </div>
      </div>
    </div>
  );
  return href ? <a href={href}>{Inner}</a> : Inner;
}
